import React, { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Menu, X, ChevronRight, Sun, Moon } from 'lucide-react';
// import logo from '../../../assets/logo/SMECAP (1).png';

import logo from '../../../assets/logo/optiii.png';
import logoWhite from '../../../assets/logo/optiii_white.png';


import { ThemeContext } from '../../../ThemeLayout';

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { mode, setMode } = useContext(ThemeContext);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const items = [
    { label: 'Services', href: '#services' },
    { label: 'Approach', href: '#approach' },
    { label: 'Process', href: '#process' },
    { label: 'Team', href: '#team' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <NavWrapper
      as={motion.nav}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <NavInner $scrolled={scrolled}>
        {/* LOGO */}
        {
          mode !== 'dark' ? (
             <LogoWrap onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Logo src={logo} alt="SME Cap Advisory" />
            </LogoWrap>
          ) : (
            <LogoWrap onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <LogoWhite src={logoWhite} alt="SME Cap Advisory" />
            </LogoWrap>
          )
        }
       

        {/* DESKTOP MENU */}
        <DesktopMenu>
          {items.map(i => (
            <NavLink key={i.label} href={i.href}>
              {i.label}
            </NavLink>
          ))}
        </DesktopMenu>

        {/* RIGHT ACTIONS */}
        <RightActions>
          {/* THEME TOGGLE */}
          <ThemeToggle
            aria-label="Toggle theme"
            onClick={() => setMode(m => (m === 'dark' ? 'light' : 'dark'))}
          >
            {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </ThemeToggle>

          {/* MOBILE TOGGLE */}
          <MobileToggle onClick={() => setMobileOpen(v => !v)}>
            {mobileOpen ? <X /> : <Menu />}
          </MobileToggle>
        </RightActions>
      </NavInner>

      {/* MOBILE MENU */}
      {mobileOpen && (
        <MobileMenu
          as={motion.div}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          {items.map(i => (
            <MobileLink
              key={i.label}
              href={i.href}
              onClick={() => setMobileOpen(false)}
            >
              {i.label}
              <ChevronRight size={16} />
            </MobileLink>
          ))}

          {/* MOBILE THEME TOGGLE */}
          <MobileThemeToggle
            onClick={() => {
              setMode(m => (m === 'dark' ? 'light' : 'dark'));
              setMobileOpen(false);
            }}
          >
            {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            Switch to {mode === 'dark' ? 'light' : 'dark'} mode
          </MobileThemeToggle>
        </MobileMenu>
      )}
    </NavWrapper>
  );
};

export default NavBar;


const NavWrapper = styled.div`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 5000;
`;

const NavInner = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;

  background: ${({ theme, $scrolled }) =>
    $scrolled ? theme.card : 'transparent'};

  backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? 'blur(14px)' : 'none'};

  transition: background 0.3s ease, backdrop-filter 0.3s ease;
`;

const LogoWrap = styled.div`
  cursor: pointer;
`;

const Logo = styled.img`
  height: 40px;
`;


const LogoWhite = styled.img`
  height: 40px;
  padding:3px;
`;

const DesktopMenu = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled.a`
  font-size: 15px;
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.bgAlt};
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ThemeToggle = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text};
  cursor: pointer;

  @media (max-width: 900px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  background: ${({ theme }) => theme.bgAlt};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MobileLink = styled.a`
  padding: 14px 16px;
  border-radius: 12px;
  text-decoration: none;
  color: ${({ theme }) => theme.text};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.card};
  }
`;

const MobileThemeToggle = styled.button`
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: none;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
`;
